import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Button, Paper, Grid } from "@material-ui/core";
import ResponsiveTable from "../../table/ResponsiveTable";
import ConfirmationModal from "../../modal/ConfirmationModal";
import TableRowProfile from "../../util/TableRowProfile";
import { ProfileType } from "../../../types/enum";
import image from "../../../helper/image";

@inject("agentVenueStore", "templateStore", "userInvitationStore", "profileStore")
@observer
class SentRequestsTable extends Component {
    state = {
        deleteRequestModalState: false,
        deleteInvitationModalState: false,
        request: undefined,
        venueName: "this venue"
    };

    columns = [
        { key: "venue", label: "Venue", width: "35%" },
        { key: "bookingDay", label: "Booking Days", width: "25%" },
        { key: "status", label: "Status", width: "15%" },
        { key: "action", label: "", width: "25%" }
    ];

    mapBookingDay = data => {
        var bookingDay = "";
        if (data) {
            if (data.monday) bookingDay += "Mon, ";
            if (data.tuesday) bookingDay += "Tue, ";
            if (data.wednesday) bookingDay += "Wed, ";
            if (data.thursday) bookingDay += "Thu, ";
            if (data.friday) bookingDay += "Fri, ";
            if (data.saturday) bookingDay += "Sat, ";
            if (data.sunday) bookingDay += "Sun, ";
        }
        return bookingDay.substr(0, bookingDay.length - 2);
    };

    safeParseJSON = (str) => {
        if (!str) return {};
        try {
            return JSON.parse(str);
        } catch (e) {
            return {};
        }
    };

    mapRowData = () => {
        const rows = [];
        const { invitations } = this.props.userInvitationStore;
        const { pendingAgentVenues } = this.props.agentVenueStore;
        
        const processedVenueIds = new Set();
        const processedVenueNames = new Set();

        if (pendingAgentVenues && pendingAgentVenues.length > 0) {
            pendingAgentVenues
                .filter(data => data && data.primaryProfile?.type === ProfileType.Agent.ordinal)
                .forEach(data => {
                    if (!data || !data.primaryProfile) return;
                    
                    var row = {};
                    var venue = data.primaryProfile?.type === ProfileType.Venue.ordinal 
                        ? data.primaryProfile 
                        : data.secondaryProfile;

                    if (venue?.id) {
                        processedVenueIds.add(String(venue.id));
                    }
                    if (venue?.fullName) {
                        processedVenueNames.add(venue.fullName.toLowerCase().trim());
                    }

                    row.id = "p" + data.id;
                    row.data = data;
                    row.style = { backgroundColor: "#fffde7" };

                    var jsonBookingDay = this.safeParseJSON(data.rules);
                    row.bookingDay = this.mapBookingDay(jsonBookingDay);
                    row.venue = (
                        <TableRowProfile 
                            profileId={venue?.id} 
                            name={venue?.fullName} 
                            avatarUrl={image.getProfileImage(venue)} 
                        />
                    );
                    row.status = <span style={{ color: "#ef6c00", fontWeight: 500 }}>Pending</span>;
                    row.action = (
                        <Button color="primary" size="small" onClick={() => this.openDeleteRequestModal(data)}>
                            Remove Request
                        </Button>
                    );
                    rows.push(row);
                });
        }

        if (invitations && invitations.length > 0) {
            invitations.forEach(data => {
                if (!data) return;
                
                if (data.invitedProfileId && processedVenueIds.has(String(data.invitedProfileId))) {
                    return;
                }
                
                if (data.invitedVenue && processedVenueNames.has(data.invitedVenue.toLowerCase().trim())) {
                    return;
                }

                var row = {};
                row.id = "i" + data.id;
                row.data = data;
                row.style = { backgroundColor: "#fffde7" };

                var jsonBookingDay = this.safeParseJSON(data.additionalData);
                row.bookingDay = this.mapBookingDay(jsonBookingDay);
                row.venue = <TableRowProfile name={data.invitedVenue} />;
                row.status = <span style={{ color: "#1565c0", fontWeight: 500 }}>Invited</span>;
                row.action = (
                    <Button size="small" color="primary" onClick={() => this.openDeleteInvitationModal(data)}>
                        Cancel Invitation
                    </Button>
                );
                rows.push(row);
            });
        }

        return rows;
    };

    openDeleteInvitationModal = invitation => {
        this.setState({
            deleteInvitationModalState: true,
            request: invitation,
            venueName: invitation.invitedVenue
        });
    };

    closeDeleteInvitationModal = () => {
        this.setState({
            deleteInvitationModalState: false,
            request: undefined,
            venueName: "this venue"
        });
    };

    handleDeleteInvitation = () => {
        this.props.templateStore.showLoadingScreen();
        this.props.userInvitationStore.deleteInvitation(this.state.request).then(() => {
            this.props.templateStore.hideLoadingScreen();
            this.props.templateStore.openSnackbar(`Invitation for ${this.state.venueName} has been removed.`);
            this.closeDeleteInvitationModal();
        });
    };

    openDeleteRequestModal = request => {
        this.setState({
            deleteRequestModalState: true,
            request: request,
            venueName: request.secondaryProfile?.fullName || "this venue"
        });
    };

    closeDeleteRequestModal = () => {
        this.setState({
            deleteRequestModalState: false,
            request: undefined
        });
    };

    handleDeleteRequest = () => {
        this.props.agentVenueStore.removeAgentVenueRequest(this.state.request.id).then(() => {
            this.props.templateStore.openSnackbar(`Venue relation request has been deleted.`);
            this.closeDeleteRequestModal();
        });
    };

    render() {
        const rows = this.mapRowData();

        return (
            <Fragment>
                <Paper style={{ padding: 24, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    {rows.length > 0 ? (
                        <ResponsiveTable 
                            columns={this.columns} 
                            rows={rows} 
                            showToolbar={true}
                            title="Sent Requests"
                        />
                    ) : (
                        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>📤</div>
                            <div style={{ fontSize: 16, marginBottom: 8 }}>No sent requests</div>
                            <div style={{ fontSize: 14 }}>
                                Use the "Add Venue" button to invite venues to your roster.
                            </div>
                        </div>
                    )}
                </Paper>

                <ConfirmationModal
                    open={this.state.deleteInvitationModalState}
                    title={"Cancel Venue Invitation"}
                    message={`<p>Are you sure you want to cancel the invitation to <b>${this.state.venueName}</b>?</p>`}
                    closeHandler={this.closeDeleteInvitationModal}
                    confirmationHandler={this.handleDeleteInvitation}
                    confirmationLabel="Cancel Invitation"
                />

                <ConfirmationModal
                    open={this.state.deleteRequestModalState}
                    title={"Withdraw Venue Request"}
                    message={"Are you sure you would like to withdraw your request to manage this venue?"}
                    closeHandler={this.closeDeleteRequestModal}
                    confirmationHandler={this.handleDeleteRequest}
                    confirmationLabel="Withdraw"
                />
            </Fragment>
        );
    }
}

export default SentRequestsTable;
